<template>
  <section class="user-page">
    <div class="page-header">
      <div>
        <h2>用户列表</h2>
        <p>统一查看前台用户账号，并支持绑定企业、设置角色和账号启停。</p>
      </div>
    </div>

    <div class="toolbar-card">
      <div class="toolbar-grid">
        <el-input v-model="filters.keyword" clearable placeholder="搜索用户名 / 邮箱 / 手机号" @keyup.enter="searchUsers" />
        <el-select v-model="filters.status" clearable placeholder="账号状态">
          <el-option label="全部" :value="null" />
          <el-option label="启用" :value="1" />
          <el-option label="禁用" :value="2" />
        </el-select>
        <el-select v-model="filters.certificationStatus" clearable placeholder="企业认证状态">
          <el-option label="全部" :value="null" />
          <el-option label="认证中" :value="1" />
          <el-option label="已认证" :value="2" />
          <el-option label="认证失败" :value="3" />
        </el-select>
        <el-date-picker
          v-model="filters.dateRange"
          type="daterange"
          value-format="x"
          range-separator="至"
          start-placeholder="注册开始日期"
          end-placeholder="注册结束日期"
        />
      </div>
      <div class="toolbar-actions">
        <el-button type="primary" @click="searchUsers">查询</el-button>
        <el-button @click="resetFilters">重置</el-button>
        <el-button :loading="exporting" @click="exportUsers">导出</el-button>
        <el-button type="primary" plain @click="openDialog()">新增账号</el-button>
      </div>
    </div>

    <div class="table-card">
      <el-table v-loading="loading" :data="list" border>
        <el-table-column prop="id" label="账号ID" width="110" />
        <el-table-column prop="username" label="用户名" min-width="140" />
        <el-table-column prop="email" label="邮箱" min-width="200" show-overflow-tooltip />
        <el-table-column prop="phone" label="手机号" width="140" />
        <el-table-column prop="companyName" label="所属企业" min-width="180" show-overflow-tooltip>
          <template #default="{ row }">{{ row.companyName || '-' }}</template>
        </el-table-column>
        <el-table-column prop="roleName" label="角色" width="120" />
        <el-table-column label="企业认证状态" width="120">
          <template #default="{ row }">
            <el-tag :type="certificationTagType(row.certificationStatus)">{{ row.certificationStatusDesc || '-' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="账号状态" width="100">
          <template #default="{ row }">
            <el-tag :type="row.status === 1 ? 'success' : 'danger'">{{ row.statusName }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="注册时间" width="170">
          <template #default="{ row }">{{ formatTime(row.createTime) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="210" fixed="right">
          <template #default="{ row }">
            <el-button link type="primary" @click="openDetail(row)">查看</el-button>
            <el-button link type="primary" @click="openDialog(row)">编辑</el-button>
            <el-button link :type="row.status === 1 ? 'danger' : 'primary'" @click="toggleStatus(row)">
              {{ row.status === 1 ? '禁用' : '启用' }}
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <Pagination :page="page" :size="size" :total="total" label="账号" @current-change="handlePageChange" />

    <el-dialog v-model="dialog.visible" :title="dialog.form.id ? '编辑账号' : '新增账号'" width="620px" destroy-on-close>
      <el-form label-position="top">
        <div class="dialog-grid">
          <el-form-item label="名" required>
            <el-input v-model="dialog.form.firstName" placeholder="请输入名" />
          </el-form-item>
          <el-form-item label="姓" required>
            <el-input v-model="dialog.form.lastName" placeholder="请输入姓" />
          </el-form-item>
          <el-form-item label="邮箱" required>
            <el-input v-model="dialog.form.email" placeholder="请输入邮箱" />
          </el-form-item>
          <el-form-item label="手机号">
            <el-input v-model="dialog.form.phone" placeholder="请输入手机号" />
          </el-form-item>
          <el-form-item label="角色" required>
            <el-select v-model="dialog.form.role" placeholder="请选择角色">
              <el-option v-for="item in roleOptions" :key="item.value" :label="item.label" :value="item.value" />
            </el-select>
          </el-form-item>
          <el-form-item class="dialog-grid-wide" label="所属企业">
            <el-select v-model="dialog.form.companyId" clearable filterable placeholder="请选择企业">
              <el-option v-for="item in customerOptions" :key="item.id" :label="item.name" :value="item.id" />
            </el-select>
          </el-form-item>
          <el-form-item v-if="!dialog.form.id" class="dialog-grid-wide" label="初始密码" required>
            <el-input v-model="dialog.form.password" type="password" show-password placeholder="6-20位，含大小写字母和数字" />
          </el-form-item>
        </div>
      </el-form>
      <template #footer>
        <el-button @click="dialog.visible = false">取消</el-button>
        <el-button type="primary" :loading="dialog.loading" @click="submitUser">保存</el-button>
      </template>
    </el-dialog>

    <el-drawer
      v-model="detailDrawer.visible"
      size="520px"
      :close-on-click-modal="false"
      :destroy-on-close="false"
    >
      <template #header>
        <div class="drawer-header">
          <div>
            <h3>账号详情</h3>
            <p>{{ detailDrawer.data?.username || '-' }}</p>
          </div>
          <div class="drawer-header-tags">
            <el-tag v-if="detailDrawer.data" :type="detailDrawer.data.status === 1 ? 'success' : 'danger'">
              {{ detailDrawer.data.statusName }}
            </el-tag>
          </div>
        </div>
      </template>

      <div v-if="detailDrawer.data" class="drawer-body">
        <div class="info-card">
          <div class="section-title">账号信息</div>
          <div class="info-grid">
            <div class="info-item">
              <span>账号ID</span>
              <strong>{{ detailDrawer.data.id }}</strong>
            </div>
            <div class="info-item">
              <span>注册时间</span>
              <strong>{{ formatTime(detailDrawer.data.createTime) }}</strong>
            </div>
            <div class="info-item">
              <span>用户名</span>
              <strong>{{ detailDrawer.data.username || '-' }}</strong>
            </div>
            <div class="info-item">
              <span>邮箱</span>
              <strong>{{ detailDrawer.data.email || '-' }}</strong>
            </div>
            <div class="info-item">
              <span>手机号</span>
              <strong>{{ detailDrawer.data.phone || '-' }}</strong>
            </div>
            <div class="info-item">
              <span>角色</span>
              <strong>{{ detailDrawer.data.roleName || '-' }}</strong>
            </div>
            <div class="info-item">
              <span>账号状态</span>
              <strong>{{ detailDrawer.data.statusName || '-' }}</strong>
            </div>
            <div class="info-item">
              <span>企业认证状态</span>
              <strong>{{ detailDrawer.data.certificationStatusDesc || '-' }}</strong>
            </div>
            <div class="info-item info-item-wide">
              <span>所属企业</span>
              <strong>{{ detailDrawer.data.companyName || '未绑定企业' }}</strong>
            </div>
          </div>
        </div>
      </div>

      <template #footer>
        <div class="drawer-footer">
          <el-button @click="detailDrawer.visible = false">关闭</el-button>
          <el-button type="primary" @click="openDialog(detailDrawer.data)">编辑账号</el-button>
        </div>
      </template>
    </el-drawer>
  </section>
</template>

<script setup>
import { onMounted, reactive, ref } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import * as Request from '@/assets/request'
import Pagination from '@/components/spagination'

const roleOptions = [
  { label: '普通用户', value: 30 },
  { label: '客户管理员', value: 70 },
]

const PASSWORD_REG = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{6,20}$/
const PASSWORD_ERROR_TEXT = '密码长度6-20位，需包含大小写字母和数字'
const validatePassword = (password) => PASSWORD_REG.test(password || '')

const filters = reactive({
  keyword: '',
  status: null,
  certificationStatus: null,
  dateRange: [],
})

const list = ref([])
const loading = ref(false)
const exporting = ref(false)
const page = ref(1)
const size = ref(10)
const total = ref(0)
const customerOptions = ref([])

const dialog = reactive({
  visible: false,
  loading: false,
  form: {
    id: null,
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    phone: '',
    password: '',
    role: 30,
    companyId: null,
  },
})

const detailDrawer = reactive({
  visible: false,
  data: null,
})

const formatTime = (time) => {
  if (!time) {
    return '-'
  }
  const date = new Date(Number(time))
  if (Number.isNaN(date.getTime())) {
    return '-'
  }
  const pad = (value) => `${value}`.padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

const certificationTagType = (status) => {
  if (status === 1) return 'warning'
  if (status === 2) return 'success'
  if (status === 3) return 'danger'
  return 'info'
}

const splitUsername = (username = '') => {
  const names = `${username || ''}`.trim().split(/\s+/, 2)
  return {
    firstName: names[0] || '',
    lastName: names[1] || '',
  }
}

const buildDisplayName = (firstName, lastName, fallback = '') => {
  return [firstName, lastName].map((item) => `${item || ''}`.trim()).filter(Boolean).join(' ') || `${fallback || ''}`.trim()
}

const fetchCustomerOptions = async () => {
  const result = await Request.post({
    url: '/company/customer/options',
    data: {},
  })
  if (result?.status === 200) {
    customerOptions.value = result.data || []
  }
}

const fetchUsers = async () => {
  loading.value = true
  const result = await Request.post({
    url: '/user/search',
    data: buildQueryPayload(),
  })
  loading.value = false
  if (result?.status === 200) {
    list.value = result.data.list || []
    total.value = result.data.total || 0
  }
}

const buildQueryPayload = ({ includePagination = true } = {}) => {
  const [startCreateTime, endCreateTime] = filters.dateRange || []
  return {
    keyword: filters.keyword?.trim() || undefined,
    status: filters.status ?? undefined,
    certificationStatus: filters.certificationStatus ?? undefined,
    startCreateTime: startCreateTime ? Number(startCreateTime) : undefined,
    endCreateTime: endCreateTime ? Number(endCreateTime) : undefined,
    frontOnly: true,
    ...(includePagination ? { page: page.value, size: size.value } : {}),
  }
}

const parseFileName = (response, fallback) => {
  const header = response?.headers?.['content-disposition'] || ''
  const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i)
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1])
  }
  const normalMatch = header.match(/filename="?([^"]+)"?/i)
  if (normalMatch?.[1]) {
    return decodeURIComponent(normalMatch[1])
  }
  return fallback
}

const exportUsers = async () => {
  exporting.value = true
  const response = await Request.download({
    url: '/user/export',
    data: buildQueryPayload({ includePagination: false }),
  })
  exporting.value = false
  if (!response) {
    return
  }
  const blob = new Blob([response.data], { type: response.data?.type || 'application/octet-stream' })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = parseFileName(response, `注册账号列表-${Date.now()}.xlsx`)
  link.click()
  URL.revokeObjectURL(link.href)
  ElMessage.success('账号数据导出完成')
}

const searchUsers = () => {
  page.value = 1
  fetchUsers()
}

const resetFilters = () => {
  filters.keyword = ''
  filters.status = null
  filters.certificationStatus = null
  filters.dateRange = []
  searchUsers()
}

const handlePageChange = (current) => {
  page.value = current
  fetchUsers()
}

const resetDialogForm = () => {
  dialog.form = {
    id: null,
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    phone: '',
    password: '',
    role: 30,
    companyId: null,
  }
}

const openDialog = (row) => {
  dialog.visible = true
  dialog.form = {
    id: row?.id || null,
    firstName: row?.firstName || splitUsername(row?.username).firstName,
    lastName: row?.lastName || splitUsername(row?.username).lastName,
    username: row?.username || '',
    email: row?.email || '',
    phone: row?.phone || '',
    password: '',
    role: row?.roleId || 30,
    companyId: row?.companyId || null,
  }
}

const openDetail = (row) => {
  detailDrawer.data = row
  detailDrawer.visible = true
}

const submitUser = async () => {
  const firstName = dialog.form.firstName?.trim()
  const lastName = dialog.form.lastName?.trim()
  const payload = {
    id: dialog.form.id || undefined,
    firstName,
    lastName,
    username: buildDisplayName(firstName, lastName, dialog.form.username),
    email: dialog.form.email?.trim(),
    phone: dialog.form.phone?.trim() || undefined,
    password: dialog.form.password || undefined,
    role: dialog.form.role,
    companyId: dialog.form.companyId || undefined,
  }
  if (!payload.firstName || !payload.lastName || !payload.username || !payload.email || !payload.role || (!dialog.form.id && !payload.password)) {
    ElMessage.error('请完整填写账号信息')
    return
  }
  if (payload.password && !validatePassword(payload.password)) {
    ElMessage.error(PASSWORD_ERROR_TEXT)
    return
  }
  dialog.loading = true
  const result = await Request.post({
    url: dialog.form.id ? '/user/update' : '/user/add',
    data: payload,
  })
  dialog.loading = false
  if (result?.status === 200) {
    ElMessage.success('账号保存成功')
    dialog.visible = false
    resetDialogForm()
    fetchUsers()
  }
}

const toggleStatus = async (row) => {
  const target = row.status === 1 ? 'disable' : 'enable'
  const actionText = row.status === 1 ? '禁用' : '启用'
  try {
    await ElMessageBox.confirm(`确定${actionText}该账号吗？`, `${actionText}确认`, { type: 'warning' })
  } catch (error) {
    return
  }
  const result = await Request.post({
    url: `/user/${target}`,
    data: [row.id],
  })
  if (result?.status === 200) {
    ElMessage.success(`账号已${actionText}`)
    fetchUsers()
    if (detailDrawer.visible && detailDrawer.data?.id === row.id) {
      detailDrawer.data = {
        ...detailDrawer.data,
        status: row.status === 1 ? 2 : 1,
        statusName: row.status === 1 ? '禁用' : '启用',
      }
    }
  }
}

onMounted(async () => {
  resetDialogForm()
  await fetchCustomerOptions()
  fetchUsers()
})
</script>

<style scoped lang="less">
.user-page {
  .page-header {
    margin-bottom: 20px;

    h2 {
      margin-bottom: 4px;
      font-size: 20px;
      line-height: 28px;
      color: #1f2937;
    }

    p {
      font-size: 13px;
      color: #6b7280;
    }
  }

  .toolbar-card,
  .table-card,
  .info-card {
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 16px;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.05);
  }

  .toolbar-card {
    padding: 12px 16px;
    margin-bottom: 12px;
  }

  .toolbar-grid {
    display: grid;
    grid-template-columns: 2fr 1fr 1fr 1.5fr;
    gap: 12px;
    align-items: center;
  }

  .toolbar-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 12px;
  }

  .table-card {
    padding: 12px;
  }

  .dialog-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0 12px;
  }

  .dialog-grid-wide {
    grid-column: 1 / -1;
  }

  :deep(.el-form-item__label) {
    font-size: 13px;
    color: #4b5563;
    padding-bottom: 4px;
  }

  :deep(.el-input__inner),
  :deep(.el-input) {
    font-size: 13px;
  }

  .drawer-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;

    h3 {
      margin-bottom: 4px;
      font-size: 22px;
      color: #111827;
    }

    p {
      color: #6b7280;
    }
  }

  .drawer-header-tags {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .drawer-body {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .info-card {
    padding: 16px;
  }

  .section-title {
    margin-bottom: 14px;
    font-size: 16px;
    font-weight: 600;
    color: #111827;
  }

  .info-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
  }

  .info-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 10px 12px;
    background: #f8fafc;
    border-radius: 8px;

    span {
      font-size: 12px;
      color: #6b7280;
    }

    strong {
      font-size: 13px;
      line-height: 20px;
      color: #111827;
      word-break: break-word;
    }
  }

  .info-item-wide {
    grid-column: 1 / -1;
  }

  .drawer-footer {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    width: 100%;
  }

  @media (max-width: 1200px) {
    .toolbar-grid {
      grid-template-columns: 1fr 1fr;
    }
  }

  @media (max-width: 900px) {
    .toolbar-grid,
    .dialog-grid,
    .info-grid {
      grid-template-columns: 1fr;
    }

    .toolbar-actions {
      flex-direction: column;
      align-items: stretch;
    }
  }
}
</style>
